// Flat, dot-namespaced translation keys. {var} placeholders are substituted
// by LanguageContext's t(key, vars) — left untouched here per language.
// Scope: the front page and the two swap widgets (SwapForm, SplitNowSwap,
// AssetPicker). Network/API error strings from services/api.js are not
// covered — those come from the backend and can't be localized client-side.

export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
];

export const translations = {
  en: {
    'common.youSend': 'You send',
    'common.youReceive': 'You receive',
    'common.getQuote': 'Get quote',
    'common.exchangeNow': 'Exchange now',
    'common.refundAddressLabel': 'Refund address (optional)',
    'common.chooseANetwork': 'Choose a network',
    'common.receiveEstimateLabel': 'You will receive (estimate)',

    'swapForm.amountLabel': 'Amount',
    'swapForm.findingRate': 'Finding the best rate...',
    'swapForm.yourAddressLabel': 'Your {asset} address',
    'swapForm.pasteAddressPlaceholder': "Paste the {asset} address you're receiving to",
    'swapForm.addressHelp': "This is where we'll send your {asset}. Double-check it — it can't be undone.",
    'swapForm.refundPlaceholder': 'Your {asset} address, in case something goes wrong',
    'swapForm.refundHelp': "If the swap can't complete, we'll return your {asset} here.",
    'swapForm.processing': 'Starting your exchange...',
    'swapForm.loadingCoins': 'Loading available coins...',
    'swapForm.enterAddressError': 'Enter the {asset} address you want to receive to.',
    'swapForm.flipAria': 'Swap direction',

    'splitNow.privateUntraceable': 'Private & untraceable',
    'splitNow.minMax': 'Min 0.1 XMR · Max 10,000 XMR',
    'splitNow.viaExchange': 'Via exchange',
    'splitNow.chooseExchange': 'Choose an exchange',
    'splitNow.chooseCoin': 'Choose a coin',
    'splitNow.minLimit': 'Min {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': 'Paste your {asset} address',
    'splitNow.addressHelp': 'Make sure this address is on the network you selected above.',
    'splitNow.refundPlaceholder': 'Your XMR address, in case something goes wrong',
    'splitNow.working': 'Working on it...',
    'splitNow.searchExchanges': 'Search exchanges...',
    'splitNow.searchCoins': 'Search coins...',
    'splitNow.loadError': "Couldn't load the coin list. Please refresh and try again.",
    'splitNow.minAmountError': 'Enter at least 0.1 XMR to get a quote.',
    'splitNow.chooseCoinError': 'Choose which coin you want to receive.',
    'splitNow.quoteError': "Couldn't get a quote right now. Please try again in a moment.",
    'splitNow.enterAddressError': 'Enter the {asset} address to receive to.',
    'splitNow.orderError': "Couldn't start that exchange. Please try again.",
    'splitNow.refreshQuoteAria': 'Refresh quote',

    'picker.searchPlaceholder': 'Search coin or network...',
    'picker.noMatch': 'No coins match "{query}"',
    'picker.popular': 'Popular',
    'picker.browseByNetwork': 'Browse by network',
    'picker.noNetworks': 'No networks available',
    'picker.noCoinsOnNetwork': 'No coins found on this network',
    'picker.coinsCount': '{count} coins',
    'picker.backAria': 'Back',
    'picker.closeAria': 'Close',
    'picker.clearSearchAria': 'Clear search',
    'picker.defaultTitle': 'Choose a coin',

    'theme.toLight': 'Switch to light mode',
    'theme.toDark': 'Switch to dark mode',
    'lang.selectAria': 'Choose language',

    'home.hero.title': "Swap crypto without the confusing part.",
    'home.hero.subtitle': 'Pick what you have, pick what you want, and send it. No account and no sign-up.',
    'home.mode.any.label': 'Any Crypto',
    'home.mode.any.sub': 'any coin to any coin',
    'home.mode.private.label': 'XMR > CEX',
    'home.mode.private.sub': 'Send XMR get a CEX output',
    'home.mode.any.desc': 'Exchange between any of our supported coins and networks. We find the best rate for you automatically.',
    'home.mode.private.desc': 'Start your wallet off fresh. No need to worry about high aml from address poisoning.',
    'home.how.title': 'How it works',
    'home.how.step1.title': 'Choose your coins',
    'home.how.step1.desc': "Tell us what you're sending and what you'd like to receive — search or browse by network.",
    'home.how.step2.title': 'Get your deposit address',
    'home.how.step2.desc': "We'll show you exactly where to send your crypto, with a QR code you can scan from your wallet.",
    'home.how.step3.title': 'Sit back',
    'home.how.step3.desc': 'We handle the exchange automatically and show you live status until your coins arrive.',
    'home.footer.noAccount': 'No account needed',
    'home.footer.nonCustodial': 'Non-custodial',
    'home.footer.bestRate': 'Best-rate routing',
    'home.footer.fineprint': "Ice Swap never holds your funds. Double-check every address before sending — crypto transactions can't be reversed.",
    'home.footer.apiDocs': 'API docs for developers',
  },

  es: {
    'common.youSend': 'Envías',
    'common.youReceive': 'Recibes',
    'common.getQuote': 'Obtener cotización',
    'common.exchangeNow': 'Cambiar ahora',
    'common.refundAddressLabel': 'Dirección de reembolso (opcional)',
    'common.chooseANetwork': 'Elige una red',
    'common.receiveEstimateLabel': 'Recibirás (estimado)',

    'swapForm.amountLabel': 'Cantidad',
    'swapForm.findingRate': 'Buscando la mejor tasa...',
    'swapForm.yourAddressLabel': 'Tu dirección de {asset}',
    'swapForm.pasteAddressPlaceholder': 'Pega la dirección de {asset} donde quieres recibir',
    'swapForm.addressHelp': 'Aquí enviaremos tu {asset}. Verifícala bien — no se puede deshacer.',
    'swapForm.refundPlaceholder': 'Tu dirección de {asset}, por si algo sale mal',
    'swapForm.refundHelp': 'Si el cambio no se completa, te devolveremos tu {asset} aquí.',
    'swapForm.processing': 'Iniciando tu cambio...',
    'swapForm.loadingCoins': 'Cargando monedas disponibles...',
    'swapForm.enterAddressError': 'Ingresa la dirección de {asset} donde quieres recibir.',
    'swapForm.flipAria': 'Invertir dirección',

    'splitNow.privateUntraceable': 'Privado y no rastreable',
    'splitNow.minMax': 'Mín. 0.1 XMR · Máx. 10,000 XMR',
    'splitNow.viaExchange': 'A través de',
    'splitNow.chooseExchange': 'Elige un exchange',
    'splitNow.chooseCoin': 'Elige una moneda',
    'splitNow.minLimit': 'Mín. {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': 'Pega tu dirección de {asset}',
    'splitNow.addressHelp': 'Asegúrate de que esta dirección sea de {exchange}, el exchange que elegiste arriba.',
    'splitNow.refundPlaceholder': 'Tu dirección de XMR, por si algo sale mal',
    'splitNow.working': 'Procesando...',
    'splitNow.searchExchanges': 'Buscar exchanges...',
    'splitNow.searchCoins': 'Buscar monedas...',
    'splitNow.loadError': 'No se pudo cargar la lista de monedas. Actualiza la página e inténtalo de nuevo.',
    'splitNow.minAmountError': 'Ingresa al menos 0.1 XMR para obtener una cotización.',
    'splitNow.chooseCoinError': 'Elige qué moneda quieres recibir.',
    'splitNow.quoteError': 'No se pudo obtener una cotización. Inténtalo de nuevo en un momento.',
    'splitNow.enterAddressError': 'Ingresa la dirección de {asset} donde quieres recibir.',
    'splitNow.orderError': 'No se pudo iniciar el cambio. Inténtalo de nuevo.',
    'splitNow.refreshQuoteAria': 'Actualizar cotización',

    'picker.searchPlaceholder': 'Buscar moneda o red...',
    'picker.noMatch': 'Ninguna moneda coincide con "{query}"',
    'picker.popular': 'Populares',
    'picker.browseByNetwork': 'Explorar por red',
    'picker.noNetworks': 'No hay redes disponibles',
    'picker.noCoinsOnNetwork': 'No se encontraron monedas en esta red',
    'picker.coinsCount': '{count} monedas',
    'picker.backAria': 'Atrás',
    'picker.closeAria': 'Cerrar',
    'picker.clearSearchAria': 'Borrar búsqueda',
    'picker.defaultTitle': 'Elige una moneda',

    'theme.toLight': 'Cambiar a modo claro',
    'theme.toDark': 'Cambiar a modo oscuro',
    'lang.selectAria': 'Elegir idioma',

    'home.hero.title': 'Cambia criptomonedas sin la parte confusa.',
    'home.hero.subtitle': 'Elige lo que tienes, elige lo que quieres, y envíalo. Sin cuenta y sin registro.',
    'home.mode.any.label': 'Cualquier cripto',
    'home.mode.any.sub': 'cualquier moneda a cualquier moneda',
    'home.mode.private.label': 'Privado (XMR)',
    'home.mode.private.sub': 'Monero, sin identificación',
    'home.mode.any.desc': 'Cambia entre cualquiera de nuestras monedas y redes compatibles. Encontramos la mejor tasa automáticamente.',
    'home.mode.private.desc': 'Envía Monero y recíbelo en cualquier exchange importante, sin nada que conecte el cambio contigo.',
    'home.how.title': 'Cómo funciona',
    'home.how.step1.title': 'Elige tus monedas',
    'home.how.step1.desc': 'Dinos qué envías y qué te gustaría recibir — busca o explora por red.',
    'home.how.step2.title': 'Obtén tu dirección de depósito',
    'home.how.step2.desc': 'Te mostraremos exactamente adónde enviar tu cripto, con un código QR que puedes escanear desde tu billetera.',
    'home.how.step3.title': 'Relájate',
    'home.how.step3.desc': 'Nosotros gestionamos el cambio automáticamente y te mostramos el estado en vivo hasta que lleguen tus monedas.',
    'home.footer.noAccount': 'Sin cuenta necesaria',
    'home.footer.nonCustodial': 'No custodial',
    'home.footer.bestRate': 'Mejor tasa automática',
    'home.footer.fineprint': 'Ice Swap nunca retiene tus fondos. Verifica cada dirección antes de enviar — las transacciones de cripto no se pueden revertir.',
    'home.footer.apiDocs': 'Documentación de la API para desarrolladores',
  },

  pt: {
    'common.youSend': 'Você envia',
    'common.youReceive': 'Você recebe',
    'common.getQuote': 'Obter cotação',
    'common.exchangeNow': 'Trocar agora',
    'common.refundAddressLabel': 'Endereço de reembolso (opcional)',
    'common.chooseANetwork': 'Escolha uma rede',
    'common.receiveEstimateLabel': 'Você receberá (estimativa)',

    'swapForm.amountLabel': 'Quantia',
    'swapForm.findingRate': 'Buscando a melhor taxa...',
    'swapForm.yourAddressLabel': 'Seu endereço de {asset}',
    'swapForm.pasteAddressPlaceholder': 'Cole o endereço de {asset} para onde deseja receber',
    'swapForm.addressHelp': 'É para aqui que enviaremos seu {asset}. Confira bem — não pode ser desfeito.',
    'swapForm.refundPlaceholder': 'Seu endereço de {asset}, caso algo dê errado',
    'swapForm.refundHelp': 'Se a troca não puder ser concluída, devolveremos seu {asset} aqui.',
    'swapForm.processing': 'Iniciando sua troca...',
    'swapForm.loadingCoins': 'Carregando moedas disponíveis...',
    'swapForm.enterAddressError': 'Informe o endereço de {asset} para onde deseja receber.',
    'swapForm.flipAria': 'Inverter direção',

    'splitNow.privateUntraceable': 'Privado e não rastreável',
    'splitNow.minMax': 'Mín. 0,1 XMR · Máx. 10.000 XMR',
    'splitNow.viaExchange': 'Via corretora',
    'splitNow.chooseExchange': 'Escolha uma corretora',
    'splitNow.chooseCoin': 'Escolha uma moeda',
    'splitNow.minLimit': 'Mín. {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': 'Cole seu endereço de {asset}',
    'splitNow.addressHelp': 'Confirme que este endereço é da {exchange}, a corretora selecionada acima.',
    'splitNow.refundPlaceholder': 'Seu endereço de XMR, caso algo dê errado',
    'splitNow.working': 'Processando...',
    'splitNow.searchExchanges': 'Buscar corretoras...',
    'splitNow.searchCoins': 'Buscar moedas...',
    'splitNow.loadError': 'Não foi possível carregar a lista de moedas. Atualize a página e tente novamente.',
    'splitNow.minAmountError': 'Informe pelo menos 0,1 XMR para obter uma cotação.',
    'splitNow.chooseCoinError': 'Escolha qual moeda deseja receber.',
    'splitNow.quoteError': 'Não foi possível obter uma cotação agora. Tente novamente em instantes.',
    'splitNow.enterAddressError': 'Informe o endereço de {asset} para onde deseja receber.',
    'splitNow.orderError': 'Não foi possível iniciar essa troca. Tente novamente.',
    'splitNow.refreshQuoteAria': 'Atualizar cotação',

    'picker.searchPlaceholder': 'Buscar moeda ou rede...',
    'picker.noMatch': 'Nenhuma moeda corresponde a "{query}"',
    'picker.popular': 'Populares',
    'picker.browseByNetwork': 'Navegar por rede',
    'picker.noNetworks': 'Nenhuma rede disponível',
    'picker.noCoinsOnNetwork': 'Nenhuma moeda encontrada nesta rede',
    'picker.coinsCount': '{count} moedas',
    'picker.backAria': 'Voltar',
    'picker.closeAria': 'Fechar',
    'picker.clearSearchAria': 'Limpar busca',
    'picker.defaultTitle': 'Escolha uma moeda',

    'theme.toLight': 'Mudar para o modo claro',
    'theme.toDark': 'Mudar para o modo escuro',
    'lang.selectAria': 'Escolher idioma',

    'home.hero.title': 'Troque criptomoedas sem a parte confusa.',
    'home.hero.subtitle': 'Escolha o que você tem, escolha o que quer, e envie. Sem conta e sem cadastro.',
    'home.mode.any.label': 'Qualquer cripto',
    'home.mode.any.sub': 'qualquer moeda para qualquer moeda',
    'home.mode.private.label': 'Privado (XMR)',
    'home.mode.private.sub': 'Monero, sem identificação',
    'home.mode.any.desc': 'Troque entre quaisquer moedas e redes compatíveis. Encontramos a melhor taxa automaticamente para você.',
    'home.mode.private.desc': 'Envie Monero e receba em qualquer grande corretora, sem nada que ligue a troca a você.',
    'home.how.title': 'Como funciona',
    'home.how.step1.title': 'Escolha suas moedas',
    'home.how.step1.desc': 'Diga o que está enviando e o que gostaria de receber — busque ou navegue por rede.',
    'home.how.step2.title': 'Obtenha seu endereço de depósito',
    'home.how.step2.desc': 'Mostraremos exatamente para onde enviar sua cripto, com um código QR que pode ser escaneado da sua carteira.',
    'home.how.step3.title': 'Relaxe',
    'home.how.step3.desc': 'Cuidamos da troca automaticamente e mostramos o status ao vivo até suas moedas chegarem.',
    'home.footer.noAccount': 'Sem necessidade de conta',
    'home.footer.nonCustodial': 'Não custodial',
    'home.footer.bestRate': 'Melhor taxa automática',
    'home.footer.fineprint': 'A Ice Swap nunca guarda seus fundos. Confira cada endereço antes de enviar — transações de cripto não podem ser revertidas.',
    'home.footer.apiDocs': 'Documentação da API para desenvolvedores',
  },

  fr: {
    'common.youSend': 'Vous envoyez',
    'common.youReceive': 'Vous recevez',
    'common.getQuote': 'Obtenir un devis',
    'common.exchangeNow': 'Échanger maintenant',
    'common.refundAddressLabel': 'Adresse de remboursement (facultatif)',
    'common.chooseANetwork': 'Choisissez un réseau',
    'common.receiveEstimateLabel': 'Vous recevrez (estimation)',

    'swapForm.amountLabel': 'Montant',
    'swapForm.findingRate': 'Recherche du meilleur taux...',
    'swapForm.yourAddressLabel': 'Votre adresse {asset}',
    'swapForm.pasteAddressPlaceholder': "Collez l'adresse {asset} de réception",
    'swapForm.addressHelp': "C'est ici que nous enverrons votre {asset}. Vérifiez-la bien — c'est irréversible.",
    'swapForm.refundPlaceholder': 'Votre adresse {asset}, en cas de problème',
    'swapForm.refundHelp': "Si l'échange ne peut pas aboutir, nous vous renverrons votre {asset} ici.",
    'swapForm.processing': 'Démarrage de votre échange...',
    'swapForm.loadingCoins': 'Chargement des cryptos disponibles...',
    'swapForm.enterAddressError': "Entrez l'adresse {asset} de réception.",
    'swapForm.flipAria': 'Inverser le sens',

    'splitNow.privateUntraceable': 'Privé et intraçable',
    'splitNow.minMax': 'Min. 0,1 XMR · Max. 10 000 XMR',
    'splitNow.viaExchange': 'Via la plateforme',
    'splitNow.chooseExchange': 'Choisissez une plateforme',
    'splitNow.chooseCoin': 'Choisissez une crypto',
    'splitNow.minLimit': 'Min. {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': 'Collez votre adresse {asset}',
    'splitNow.addressHelp': "Assurez-vous que cette adresse provient de {exchange}, la plateforme choisie ci-dessus.",
    'splitNow.refundPlaceholder': 'Votre adresse XMR, en cas de problème',
    'splitNow.working': 'Traitement en cours...',
    'splitNow.searchExchanges': 'Rechercher des plateformes...',
    'splitNow.searchCoins': 'Rechercher des cryptos...',
    'splitNow.loadError': 'Impossible de charger la liste des cryptos. Actualisez la page et réessayez.',
    'splitNow.minAmountError': 'Entrez au moins 0,1 XMR pour obtenir un devis.',
    'splitNow.chooseCoinError': 'Choisissez la crypto que vous souhaitez recevoir.',
    'splitNow.quoteError': "Impossible d'obtenir un devis pour le moment. Réessayez dans un instant.",
    'splitNow.enterAddressError': "Entrez l'adresse {asset} de réception.",
    'splitNow.orderError': 'Impossible de démarrer cet échange. Veuillez réessayer.',
    'splitNow.refreshQuoteAria': 'Actualiser le devis',

    'picker.searchPlaceholder': 'Rechercher une crypto ou un réseau...',
    'picker.noMatch': 'Aucune crypto ne correspond à « {query} »',
    'picker.popular': 'Populaires',
    'picker.browseByNetwork': 'Parcourir par réseau',
    'picker.noNetworks': 'Aucun réseau disponible',
    'picker.noCoinsOnNetwork': 'Aucune crypto trouvée sur ce réseau',
    'picker.coinsCount': '{count} cryptos',
    'picker.backAria': 'Retour',
    'picker.closeAria': 'Fermer',
    'picker.clearSearchAria': 'Effacer la recherche',
    'picker.defaultTitle': 'Choisissez une crypto',

    'theme.toLight': 'Passer en mode clair',
    'theme.toDark': 'Passer en mode sombre',
    'lang.selectAria': 'Choisir la langue',

    'home.hero.title': 'Échangez vos cryptos sans la partie compliquée.',
    'home.hero.subtitle': 'Choisissez ce que vous avez, ce que vous voulez, et envoyez. Sans compte, sans inscription.',
    'home.mode.any.label': 'Toutes cryptos',
    'home.mode.any.sub': "n'importe quelle crypto vers une autre",
    'home.mode.private.label': 'Privé (XMR)',
    'home.mode.private.sub': 'Monero, sans identité',
    'home.mode.any.desc': 'Échangez entre toutes nos cryptos et réseaux pris en charge. Nous trouvons automatiquement le meilleur taux.',
    'home.mode.private.desc': "Envoyez du Monero et recevez sur n'importe quelle grande plateforme, sans rien qui relie l'échange à vous.",
    'home.how.title': 'Comment ça marche',
    'home.how.step1.title': 'Choisissez vos cryptos',
    'home.how.step1.desc': 'Indiquez ce que vous envoyez et ce que vous souhaitez recevoir — recherchez ou parcourez par réseau.',
    'home.how.step2.title': 'Obtenez votre adresse de dépôt',
    'home.how.step2.desc': 'Nous vous indiquerons exactement où envoyer vos cryptos, avec un QR code à scanner depuis votre portefeuille.',
    'home.how.step3.title': 'Détendez-vous',
    'home.how.step3.desc': "Nous gérons l'échange automatiquement et affichons le statut en direct jusqu'à l'arrivée de vos cryptos.",
    'home.footer.noAccount': 'Aucun compte requis',
    'home.footer.nonCustodial': 'Non dépositaire',
    'home.footer.bestRate': 'Meilleur taux automatique',
    'home.footer.fineprint': 'Ice Swap ne détient jamais vos fonds. Vérifiez chaque adresse avant l\'envoi — les transactions crypto sont irréversibles.',
    'home.footer.apiDocs': 'Documentation de l\'API pour les développeurs',
  },

  de: {
    'common.youSend': 'Sie senden',
    'common.youReceive': 'Sie erhalten',
    'common.getQuote': 'Kurs abrufen',
    'common.exchangeNow': 'Jetzt tauschen',
    'common.refundAddressLabel': 'Rückerstattungsadresse (optional)',
    'common.chooseANetwork': 'Netzwerk wählen',
    'common.receiveEstimateLabel': 'Sie erhalten (geschätzt)',

    'swapForm.amountLabel': 'Betrag',
    'swapForm.findingRate': 'Bester Kurs wird gesucht...',
    'swapForm.yourAddressLabel': 'Ihre {asset}-Adresse',
    'swapForm.pasteAddressPlaceholder': '{asset}-Adresse für den Empfang einfügen',
    'swapForm.addressHelp': 'Hierhin senden wir Ihr {asset}. Bitte genau prüfen — nicht rückgängig zu machen.',
    'swapForm.refundPlaceholder': 'Ihre {asset}-Adresse, falls etwas schiefgeht',
    'swapForm.refundHelp': 'Falls der Tausch nicht abgeschlossen werden kann, senden wir Ihr {asset} hierher zurück.',
    'swapForm.processing': 'Ihr Tausch wird gestartet...',
    'swapForm.loadingCoins': 'Verfügbare Coins werden geladen...',
    'swapForm.enterAddressError': 'Geben Sie die {asset}-Adresse für den Empfang ein.',
    'swapForm.flipAria': 'Richtung tauschen',

    'splitNow.privateUntraceable': 'Privat & nicht nachverfolgbar',
    'splitNow.minMax': 'Min. 0,1 XMR · Max. 10.000 XMR',
    'splitNow.viaExchange': 'Über Börse',
    'splitNow.chooseExchange': 'Börse wählen',
    'splitNow.chooseCoin': 'Coin wählen',
    'splitNow.minLimit': 'Min. {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': 'Ihre {asset}-Adresse einfügen',
    'splitNow.addressHelp': 'Stellen Sie sicher, dass diese Adresse zu {exchange} gehört, der oben ausgewählten Börse.',
    'splitNow.refundPlaceholder': 'Ihre XMR-Adresse, falls etwas schiefgeht',
    'splitNow.working': 'Wird bearbeitet...',
    'splitNow.searchExchanges': 'Börsen suchen...',
    'splitNow.searchCoins': 'Coins suchen...',
    'splitNow.loadError': 'Coin-Liste konnte nicht geladen werden. Seite aktualisieren und erneut versuchen.',
    'splitNow.minAmountError': 'Geben Sie mindestens 0,1 XMR ein, um einen Kurs zu erhalten.',
    'splitNow.chooseCoinError': 'Wählen Sie, welchen Coin Sie erhalten möchten.',
    'splitNow.quoteError': 'Kurs konnte gerade nicht abgerufen werden. Versuchen Sie es gleich noch einmal.',
    'splitNow.enterAddressError': 'Geben Sie die {asset}-Adresse für den Empfang ein.',
    'splitNow.orderError': 'Tausch konnte nicht gestartet werden. Bitte erneut versuchen.',
    'splitNow.refreshQuoteAria': 'Kurs aktualisieren',

    'picker.searchPlaceholder': 'Coin oder Netzwerk suchen...',
    'picker.noMatch': 'Keine Coins passen zu „{query}"',
    'picker.popular': 'Beliebt',
    'picker.browseByNetwork': 'Nach Netzwerk durchsuchen',
    'picker.noNetworks': 'Keine Netzwerke verfügbar',
    'picker.noCoinsOnNetwork': 'Keine Coins in diesem Netzwerk gefunden',
    'picker.coinsCount': '{count} Coins',
    'picker.backAria': 'Zurück',
    'picker.closeAria': 'Schließen',
    'picker.clearSearchAria': 'Suche löschen',
    'picker.defaultTitle': 'Coin wählen',

    'theme.toLight': 'Zum hellen Modus wechseln',
    'theme.toDark': 'Zum dunklen Modus wechseln',
    'lang.selectAria': 'Sprache wählen',

    'home.hero.title': 'Kryptowährungen tauschen, ganz ohne Verwirrung.',
    'home.hero.subtitle': 'Wählen Sie, was Sie haben, wählen Sie, was Sie möchten, und senden Sie es. Kein Konto, keine Anmeldung.',
    'home.mode.any.label': 'Beliebige Krypto',
    'home.mode.any.sub': 'jede Münze zu jeder Münze',
    'home.mode.private.label': 'Privat (XMR)',
    'home.mode.private.sub': 'Monero, ohne Ausweis',
    'home.mode.any.desc': 'Tauschen Sie zwischen allen unterstützten Coins und Netzwerken. Wir finden automatisch den besten Kurs für Sie.',
    'home.mode.private.desc': 'Senden Sie Monero und empfangen Sie auf jeder größeren Börse — ohne dass irgendetwas den Tausch zu Ihnen zurückverfolgen lässt.',
    'home.how.title': "So funktioniert's",
    'home.how.step1.title': 'Coins wählen',
    'home.how.step1.desc': 'Sagen Sie uns, was Sie senden und was Sie erhalten möchten — suchen oder nach Netzwerk durchsuchen.',
    'home.how.step2.title': 'Einzahlungsadresse erhalten',
    'home.how.step2.desc': 'Wir zeigen Ihnen genau, wohin Sie Ihre Krypto senden sollen, mit einem QR-Code zum Scannen aus Ihrer Wallet.',
    'home.how.step3.title': 'Zurücklehnen',
    'home.how.step3.desc': 'Wir wickeln den Tausch automatisch ab und zeigen den Live-Status, bis Ihre Coins ankommen.',
    'home.footer.noAccount': 'Kein Konto nötig',
    'home.footer.nonCustodial': 'Nicht verwahrend',
    'home.footer.bestRate': 'Automatisch bester Kurs',
    'home.footer.fineprint': 'Ice Swap verwahrt niemals Ihre Gelder. Prüfen Sie jede Adresse vor dem Senden — Krypto-Transaktionen sind nicht rückgängig zu machen.',
    'home.footer.apiDocs': 'API-Dokumentation für Entwickler',
  },

  ru: {
    'common.youSend': 'Вы отправляете',
    'common.youReceive': 'Вы получаете',
    'common.getQuote': 'Получить курс',
    'common.exchangeNow': 'Обменять сейчас',
    'common.refundAddressLabel': 'Адрес для возврата (необязательно)',
    'common.chooseANetwork': 'Выберите сеть',
    'common.receiveEstimateLabel': 'Вы получите (примерно)',

    'swapForm.amountLabel': 'Сумма',
    'swapForm.findingRate': 'Ищем лучший курс...',
    'swapForm.yourAddressLabel': 'Ваш адрес {asset}',
    'swapForm.pasteAddressPlaceholder': 'Вставьте адрес {asset} для получения',
    'swapForm.addressHelp': 'Сюда мы отправим ваш {asset}. Проверьте внимательно — отменить нельзя.',
    'swapForm.refundPlaceholder': 'Ваш адрес {asset} на случай проблем',
    'swapForm.refundHelp': 'Если обмен не завершится, мы вернём ваш {asset} на этот адрес.',
    'swapForm.processing': 'Запускаем обмен...',
    'swapForm.loadingCoins': 'Загружаем доступные монеты...',
    'swapForm.enterAddressError': 'Введите адрес {asset} для получения.',
    'swapForm.flipAria': 'Поменять направление',

    'splitNow.privateUntraceable': 'Приватно и без отслеживания',
    'splitNow.minMax': 'Мин. 0.1 XMR · Макс. 10 000 XMR',
    'splitNow.viaExchange': 'Через биржу',
    'splitNow.chooseExchange': 'Выберите биржу',
    'splitNow.chooseCoin': 'Выберите монету',
    'splitNow.minLimit': 'Мин. {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': 'Вставьте ваш адрес {asset}',
    'splitNow.addressHelp': 'Убедитесь, что этот адрес с {exchange} — биржи, выбранной выше.',
    'splitNow.refundPlaceholder': 'Ваш адрес XMR на случай проблем',
    'splitNow.working': 'Обрабатываем...',
    'splitNow.searchExchanges': 'Поиск бирж...',
    'splitNow.searchCoins': 'Поиск монет...',
    'splitNow.loadError': 'Не удалось загрузить список монет. Обновите страницу и попробуйте снова.',
    'splitNow.minAmountError': 'Введите не менее 0.1 XMR, чтобы получить курс.',
    'splitNow.chooseCoinError': 'Выберите монету, которую хотите получить.',
    'splitNow.quoteError': 'Не удалось получить курс. Попробуйте ещё раз через момент.',
    'splitNow.enterAddressError': 'Введите адрес {asset} для получения.',
    'splitNow.orderError': 'Не удалось начать обмен. Попробуйте снова.',
    'splitNow.refreshQuoteAria': 'Обновить курс',

    'picker.searchPlaceholder': 'Поиск монеты или сети...',
    'picker.noMatch': 'Нет монет по запросу «{query}»',
    'picker.popular': 'Популярные',
    'picker.browseByNetwork': 'Обзор по сетям',
    'picker.noNetworks': 'Нет доступных сетей',
    'picker.noCoinsOnNetwork': 'В этой сети монеты не найдены',
    'picker.coinsCount': '{count} монет',
    'picker.backAria': 'Назад',
    'picker.closeAria': 'Закрыть',
    'picker.clearSearchAria': 'Очистить поиск',
    'picker.defaultTitle': 'Выберите монету',

    'theme.toLight': 'Включить светлую тему',
    'theme.toDark': 'Включить тёмную тему',
    'lang.selectAria': 'Выбрать язык',

    'home.hero.title': 'Обменивайте криптовалюту без лишней сложности.',
    'home.hero.subtitle': 'Выберите, что у вас есть, выберите, что хотите получить, и отправьте. Без аккаунта и регистрации.',
    'home.mode.any.label': 'Любая крипта',
    'home.mode.any.sub': 'любая монета на любую',
    'home.mode.private.label': 'Приватно (XMR)',
    'home.mode.private.sub': 'Monero, без документов',
    'home.mode.any.desc': 'Обменивайте между любыми поддерживаемыми монетами и сетями. Мы автоматически находим лучший курс.',
    'home.mode.private.desc': 'Отправьте Monero и получите на любой крупной бирже — ничто не свяжет обмен с вами.',
    'home.how.title': 'Как это работает',
    'home.how.step1.title': 'Выберите монеты',
    'home.how.step1.desc': 'Укажите, что отправляете и что хотите получить — ищите или выбирайте по сети.',
    'home.how.step2.title': 'Получите адрес для депозита',
    'home.how.step2.desc': 'Мы покажем точный адрес для отправки криптовалюты и QR-код для сканирования из вашего кошелька.',
    'home.how.step3.title': 'Расслабьтесь',
    'home.how.step3.desc': 'Мы автоматически проведём обмен и покажем статус в реальном времени, пока не придут ваши монеты.',
    'home.footer.noAccount': 'Аккаунт не нужен',
    'home.footer.nonCustodial': 'Без хранения средств',
    'home.footer.bestRate': 'Автоматически лучший курс',
    'home.footer.fineprint': 'Ice Swap никогда не хранит ваши средства. Проверяйте каждый адрес перед отправкой — криптотранзакции необратимы.',
    'home.footer.apiDocs': 'Документация API для разработчиков',
  },

  zh: {
    'common.youSend': '您发送',
    'common.youReceive': '您收到',
    'common.getQuote': '获取报价',
    'common.exchangeNow': '立即兑换',
    'common.refundAddressLabel': '退款地址（可选）',
    'common.chooseANetwork': '选择网络',
    'common.receiveEstimateLabel': '预计收到',

    'swapForm.amountLabel': '数量',
    'swapForm.findingRate': '正在寻找最佳汇率...',
    'swapForm.yourAddressLabel': '您的 {asset} 地址',
    'swapForm.pasteAddressPlaceholder': '粘贴您接收 {asset} 的地址',
    'swapForm.addressHelp': '我们会把您的 {asset} 发送到这里，请仔细核对——一旦发送无法撤销。',
    'swapForm.refundPlaceholder': '您的 {asset} 地址，以防出现问题',
    'swapForm.refundHelp': '如果兑换无法完成，我们会把您的 {asset} 退回此地址。',
    'swapForm.processing': '正在启动兑换...',
    'swapForm.loadingCoins': '正在加载可用币种...',
    'swapForm.enterAddressError': '请输入您接收 {asset} 的地址。',
    'swapForm.flipAria': '交换方向',

    'splitNow.privateUntraceable': '私密且不可追踪',
    'splitNow.minMax': '最低 0.1 XMR · 最高 10,000 XMR',
    'splitNow.viaExchange': '通过交易所',
    'splitNow.chooseExchange': '选择交易所',
    'splitNow.chooseCoin': '选择币种',
    'splitNow.minLimit': '最低 {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': '粘贴您的 {asset} 地址',
    'splitNow.addressHelp': '请确认此地址属于您上面选择的交易所 {exchange}。',
    'splitNow.refundPlaceholder': '您的 XMR 地址，以防出现问题',
    'splitNow.working': '正在处理...',
    'splitNow.searchExchanges': '搜索交易所...',
    'splitNow.searchCoins': '搜索币种...',
    'splitNow.loadError': '无法加载币种列表，请刷新页面后重试。',
    'splitNow.minAmountError': '请至少输入 0.1 XMR 以获取报价。',
    'splitNow.chooseCoinError': '请选择您想接收的币种。',
    'splitNow.quoteError': '暂时无法获取报价，请稍后再试。',
    'splitNow.enterAddressError': '请输入您接收 {asset} 的地址。',
    'splitNow.orderError': '无法发起此次兑换，请重试。',
    'splitNow.refreshQuoteAria': '刷新报价',

    'picker.searchPlaceholder': '搜索币种或网络...',
    'picker.noMatch': '没有与"{query}"匹配的币种',
    'picker.popular': '热门',
    'picker.browseByNetwork': '按网络浏览',
    'picker.noNetworks': '暂无可用网络',
    'picker.noCoinsOnNetwork': '该网络下未找到币种',
    'picker.coinsCount': '{count} 种币',
    'picker.backAria': '返回',
    'picker.closeAria': '关闭',
    'picker.clearSearchAria': '清除搜索',
    'picker.defaultTitle': '选择币种',

    'theme.toLight': '切换到浅色模式',
    'theme.toDark': '切换到深色模式',
    'lang.selectAria': '选择语言',

    'home.hero.title': '轻松兑换加密货币，不再复杂。',
    'home.hero.subtitle': '选择您拥有的币种，选择您想要的币种，然后发送即可。无需注册，无需账户。',
    'home.mode.any.label': '任意币种',
    'home.mode.any.sub': '任意币种互换',
    'home.mode.private.label': '隐私兑换（XMR）',
    'home.mode.private.sub': '门罗币，无需身份验证',
    'home.mode.any.desc': '在我们支持的任意币种和网络之间兑换，我们会自动为您寻找最佳汇率。',
    'home.mode.private.desc': '发送门罗币，可在任意主流交易所接收，整个过程无法追溯到您。',
    'home.how.title': '使用方法',
    'home.how.step1.title': '选择币种',
    'home.how.step1.desc': '告诉我们您要发送什么、想接收什么——可以搜索，也可以按网络浏览。',
    'home.how.step2.title': '获取存款地址',
    'home.how.step2.desc': '我们会准确告诉您把加密货币发送到哪里，并提供可用钱包扫描的二维码。',
    'home.how.step3.title': '静候完成',
    'home.how.step3.desc': '我们会自动完成兑换，并实时显示状态，直到您的币种到账。',
    'home.footer.noAccount': '无需账户',
    'home.footer.nonCustodial': '非托管',
    'home.footer.bestRate': '自动最优汇率',
    'home.footer.fineprint': 'Ice Swap 从不托管您的资金。发送前请仔细核对每个地址——加密货币交易无法撤销。',
    'home.footer.apiDocs': '面向开发者的 API 文档',
  },

  ja: {
    'common.youSend': '送るコイン',
    'common.youReceive': '受け取るコイン',
    'common.getQuote': 'レートを取得',
    'common.exchangeNow': '今すぐ交換',
    'common.refundAddressLabel': '返金アドレス（任意）',
    'common.chooseANetwork': 'ネットワークを選択',
    'common.receiveEstimateLabel': '受取予定額（概算）',

    'swapForm.amountLabel': '数量',
    'swapForm.findingRate': '最適なレートを検索中...',
    'swapForm.yourAddressLabel': '{asset} の受取アドレス',
    'swapForm.pasteAddressPlaceholder': '受け取る {asset} アドレスを貼り付けてください',
    'swapForm.addressHelp': 'こちらのアドレスに {asset} をお送りします。送信後は取り消せませんので、よくご確認ください。',
    'swapForm.refundPlaceholder': '問題が発生した場合の {asset} アドレス',
    'swapForm.refundHelp': '交換が完了しない場合、{asset} をこちらのアドレスに返金します。',
    'swapForm.processing': '交換を開始しています...',
    'swapForm.loadingCoins': '利用可能なコインを読み込み中...',
    'swapForm.enterAddressError': '受け取る {asset} アドレスを入力してください。',
    'swapForm.flipAria': '方向を入れ替える',

    'splitNow.privateUntraceable': 'プライベートかつ追跡不可',
    'splitNow.minMax': '最小 0.1 XMR ・最大 10,000 XMR',
    'splitNow.viaExchange': '利用する取引所',
    'splitNow.chooseExchange': '取引所を選択',
    'splitNow.chooseCoin': 'コインを選択',
    'splitNow.minLimit': '最小 {min} {symbol}',
    'splitNow.pasteAddressPlaceholder': '{asset} アドレスを貼り付けてください',
    'splitNow.addressHelp': '上で選択した取引所「{exchange}」のアドレスであることを確認してください。',
    'splitNow.refundPlaceholder': '問題が発生した場合の XMR アドレス',
    'splitNow.working': '処理中...',
    'splitNow.searchExchanges': '取引所を検索...',
    'splitNow.searchCoins': 'コインを検索...',
    'splitNow.loadError': 'コインリストを読み込めませんでした。ページを更新して再試行してください。',
    'splitNow.minAmountError': 'レートを取得するには 0.1 XMR 以上を入力してください。',
    'splitNow.chooseCoinError': '受け取るコインを選択してください。',
    'splitNow.quoteError': '現在レートを取得できません。しばらくしてから再試行してください。',
    'splitNow.enterAddressError': '受け取る {asset} アドレスを入力してください。',
    'splitNow.orderError': '交換を開始できませんでした。再度お試しください。',
    'splitNow.refreshQuoteAria': 'レートを更新',

    'picker.searchPlaceholder': 'コインまたはネットワークを検索...',
    'picker.noMatch': '「{query}」に一致するコインはありません',
    'picker.popular': '人気',
    'picker.browseByNetwork': 'ネットワークから探す',
    'picker.noNetworks': '利用可能なネットワークがありません',
    'picker.noCoinsOnNetwork': 'このネットワークにはコインが見つかりません',
    'picker.coinsCount': '{count} 種類のコイン',
    'picker.backAria': '戻る',
    'picker.closeAria': '閉じる',
    'picker.clearSearchAria': '検索をクリア',
    'picker.defaultTitle': 'コインを選択',

    'theme.toLight': 'ライトモードに切り替え',
    'theme.toDark': 'ダークモードに切り替え',
    'lang.selectAria': '言語を選択',

    'home.hero.title': 'わかりにくさゼロで暗号資産を交換。',
    'home.hero.subtitle': 'お持ちのコインと欲しいコインを選んで送るだけ。アカウント登録は不要です。',
    'home.mode.any.label': 'どのコインも',
    'home.mode.any.sub': 'あらゆるコイン同士を交換',
    'home.mode.private.label': 'プライベート（XMR）',
    'home.mode.private.sub': 'Moneroで本人確認不要',
    'home.mode.any.desc': '対応するすべてのコイン・ネットワーク間で交換できます。最適なレートを自動的に見つけます。',
    'home.mode.private.desc': 'Moneroを送るだけで、主要な取引所で受け取れます。交換内容があなたに結び付くことはありません。',
    'home.how.title': 'ご利用の流れ',
    'home.how.step1.title': 'コインを選ぶ',
    'home.how.step1.desc': '送るコインと受け取りたいコインを教えてください。検索またはネットワークから探せます。',
    'home.how.step2.title': '入金アドレスを取得',
    'home.how.step2.desc': '送金先アドレスを正確にご案内し、ウォレットから読み取れるQRコードも表示します。',
    'home.how.step3.title': 'あとはお待ちいただくだけ',
    'home.how.step3.desc': '交換は自動で処理され、コインが届くまでリアルタイムで状況を確認できます。',
    'home.footer.noAccount': 'アカウント不要',
    'home.footer.nonCustodial': '非保管型',
    'home.footer.bestRate': '自動で最適レート',
    'home.footer.fineprint': 'Ice Swapがお客様の資金を保管することはありません。送信前に必ずアドレスをご確認ください——暗号資産の取引は取り消せません。',
    'home.footer.apiDocs': '開発者向けAPIドキュメント',
  },
};
